import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  List,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconAlertCircle, IconTrash } from '@tabler/icons-react';
import {
  CONSENT_SOURCE_LABELS,
  type AdminUserRow,
  type ConsentRecord,
  type ConsentState,
} from '@creativitas/shared';
import { toUserMessage } from '@/lib/api';
import {
  ADMIN_PRIVACY_KEY,
  adminUserConsentsKey,
  eraseUser,
  fetchUserConsents,
  withdrawUserConsent,
} from '@/features/privacy/api';
import { ADMIN_USERS_KEY } from './api';

/**
 * Согласия человека и удаление его данных — по обращению (docs/PRIVACY.md, §6).
 *
 * Обращения приходят письмом на support@: «отпишите меня», «удалите мои
 * данные». Администратор записывает отзыв с основанием или удаляет аккаунт.
 * Дать согласие за человека нельзя — это его действие, поэтому обратной
 * кнопки нет.
 */

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });

const shortText = (consent: ConsentState): string => {
  const text = consent.text ?? consent.latest?.text ?? '';
  return text.length > 90 ? `${text.slice(0, 90)}…` : text || consent.consentId;
};

function recordLine(record: ConsentRecord): string {
  const verb = record.granted ? 'дано' : 'отозвано';
  const who = record.actorDisplayName ? ` (${record.actorDisplayName})` : '';
  const revision = record.revision ? `, ред. ${record.revision}` : '';
  return `${formatDate(record.createdAt)} — ${verb} ${CONSENT_SOURCE_LABELS[record.source]}${who}${revision}`;
}

export function UserPrivacyAdmin({ row, onErased }: { row: AdminUserRow; onErased: () => void }) {
  const queryClient = useQueryClient();
  const userId = row.user.id;
  const consents = useQuery({ queryKey: adminUserConsentsKey(userId), queryFn: () => fetchUserConsents(userId) });

  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const withdraw = useMutation({
    mutationFn: (consentId: string) => withdrawUserConsent(userId, consentId, { note }),
    onSuccess: (data) => {
      queryClient.setQueryData(adminUserConsentsKey(userId), data);
      void queryClient.invalidateQueries({ queryKey: ADMIN_PRIVACY_KEY });
      setWithdrawing(null);
      setNote('');
      notifications.show({ color: 'teal', message: 'Отзыв записан' });
    },
  });

  const [eraseOpened, setEraseOpened] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const erase = useMutation({
    mutationFn: () => eraseUser(userId, { confirmEmail }),
    onSuccess: () => {
      setEraseOpened(false);
      void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
      void queryClient.invalidateQueries({ queryKey: ADMIN_PRIVACY_KEY });
      notifications.show({ color: 'teal', message: 'Аккаунт и данные удалены' });
      onErased();
    },
  });

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={5}>Согласия</Title>
        {consents.isLoading ? (
          <Text size="sm" c="var(--text-muted)">
            Загружаем...
          </Text>
        ) : consents.isError || !consents.data ? (
          <Alert color="red" icon={<IconAlertCircle size={17} />}>
            {toUserMessage(consents.error)}
          </Alert>
        ) : (
          <Stack gap="sm">
            {consents.data.consents.map((consent) => (
              <Stack key={consent.consentId} gap={4} data-testid={`admin-consent-${consent.consentId}`}>
                <Group gap="xs" wrap="nowrap" align="flex-start">
                  <Badge
                    size="sm"
                    variant="light"
                    color={consent.granted ? 'teal' : 'gray'}
                    style={{ flexShrink: 0 }}
                  >
                    {consent.granted ? 'дано' : 'нет'}
                  </Badge>
                  <Text size="sm" style={{ minWidth: 0 }}>
                    {shortText(consent)}
                    {consent.required ? (
                      <Text span size="xs" c="var(--text-muted)">
                        {' '}
                        (обязательное)
                      </Text>
                    ) : null}
                  </Text>
                </Group>
                <Text size="xs" c="var(--text-muted)">
                  {consent.latest ? recordLine(consent.latest) : 'не давалось'}
                </Text>

                {!consent.required && consent.granted ? (
                  withdrawing === consent.consentId ? (
                    <Stack gap="xs">
                      <TextInput
                        size="xs"
                        label="Основание"
                        placeholder="Письмо на support@ от 15.10.2026"
                        description="Без имени и почты — журнал хранит только факт обращения."
                        value={note}
                        onChange={(event) => setNote(event.currentTarget.value)}
                        error={withdraw.isError ? toUserMessage(withdraw.error) : undefined}
                      />
                      <Group gap="xs">
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          loading={withdraw.isPending}
                          disabled={note.trim().length < 3}
                          onClick={() => withdraw.mutate(consent.consentId)}
                        >
                          Записать отзыв
                        </Button>
                        <Button size="xs" variant="default" onClick={() => setWithdrawing(null)}>
                          Отмена
                        </Button>
                      </Group>
                    </Stack>
                  ) : (
                    <Group>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        onClick={() => {
                          withdraw.reset();
                          setWithdrawing(consent.consentId);
                        }}
                      >
                        Отозвать по обращению…
                      </Button>
                    </Group>
                  )
                ) : null}
              </Stack>
            ))}

            {consents.data.history.length > 0 ? (
              <details>
                <summary style={{ cursor: 'pointer' }}>
                  <Text span size="xs" c="var(--text-muted)">
                    История согласий ({consents.data.history.length})
                  </Text>
                </summary>
                <List size="xs" spacing={2} mt={6}>
                  {consents.data.history.map((record) => (
                    <List.Item key={record.id}>
                      <Text size="xs">
                        {record.consentId}: {recordLine(record)}
                        {record.note ? ` — ${record.note}` : ''}
                      </Text>
                    </List.Item>
                  ))}
                </List>
              </details>
            ) : null}
          </Stack>
        )}
      </Stack>

      <Stack gap="xs">
        <Title order={5}>Удаление аккаунта и данных</Title>
        <Text size="sm" c="var(--text-muted)">
          По обращению человека. Удаляется сразу и безвозвратно; повторяется само, если базу
          восстановят из старой копии. Заказы с почтой покупателя остаются — их хранят по закону.
        </Text>
        <Group>
          <Button
            size="xs"
            variant="outline"
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={() => {
              erase.reset();
              setConfirmEmail('');
              setEraseOpened(true);
            }}
          >
            Удалить аккаунт и данные…
          </Button>
        </Group>
      </Stack>

      <Modal
        opened={eraseOpened}
        onClose={() => setEraseOpened(false)}
        title={`Удалить ${row.user.displayName}?`}
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm">
            Аккаунт, вход, права и тариф, согласия, проведённые им партии и МАК-сессии вместе с
            участниками; в чужих партиях его имя заменится на «Удалённый участник». Восстановить
            нельзя.
          </Text>
          {erase.isError ? (
            <Alert color="red" icon={<IconAlertCircle size={17} />} title="Не удалось удалить">
              {toUserMessage(erase.error)}
            </Alert>
          ) : null}
          <TextInput
            label="Почта аккаунта"
            description={`Введите ${row.user.email} — чтобы не удалить другого человека.`}
            value={confirmEmail}
            onChange={(event) => setConfirmEmail(event.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEraseOpened(false)}>
              Отмена
            </Button>
            <Button
              color="red"
              loading={erase.isPending}
              disabled={confirmEmail.trim().toLowerCase() !== row.user.email.toLowerCase()}
              onClick={() => erase.mutate()}
            >
              Удалить навсегда
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
